import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import RemoveHomecareModal from "./modals/RemoveHomecareModal";
import Loader from "../../common/Loader/Loader";
import { App } from "../../../routes/Constants";
import APIVariables from "../../../services/api-variables";
import { handleCatch } from "../../../shared/helpers";
import { errorMessages } from "../../../constants/errorMessages.constants";
import Axios from "../../../services/Axios";
import DataTable from "@/global/tables/DataTable";
import { getHomecareListColumns } from "./HomecareListColumns";

const HomecareList = () => {
  const [modalIsOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedHomecare, setSelectedHomecare] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [items, setItems] = useState([]);

  const navigate = useNavigate();

  const fetchEntireHomecareList = async () => {
    setIsLoading(true);
    try {
      let acc = [];
      let nextUrl = APIVariables.HCP_HOMECARES;

      while (nextUrl) {
        const res = await Axios.get(nextUrl);
        if (res.status !== 200) break;

        const { data } = res;

        if (Array.isArray(data)) {
          acc = data;
          nextUrl = null;
        } else {
          const chunk = data?.results ?? [];
          acc = acc.concat(chunk);
          nextUrl = data?.next || null;
        }
      }

      setItems(acc);
    } catch (error) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEntireHomecareList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addHomecare = () => {
    navigate(App.HOMECARE_ADD);
  };

  const openModalRemoveHomecare = useCallback((e, id) => {
    e.stopPropagation();
    setSelectedHomecare(id);
    setIsModalOpen(true);
  }, []);

  const redirectToEditHomecare = useCallback(
    (row) => {
      navigate(App.HOMECARE_EDIT_DEFAULT + `${row.connected_user_id}`);
    },
    [navigate]
  );

  const closeModal = () => {
    setSelectedHomecare("");
    setErrorMessage("");
    setIsModalOpen(false);
  };

  const removeHomecare = async () => {
    setErrorMessage("");
    await Axios.delete(APIVariables.HCP_HOMECARE_DELETE + selectedHomecare)
      .then((res) => {
        const { status } = res;
        if (status === 200) {
          const newItems = items.filter((item) => {
            return item.connected_user_id !== selectedHomecare;
          });
          setItems(newItems);
          closeModal();
        }
      })
      .catch(function (error) {
        const errors = error.response.data;
        if (typeof errors === "string") {
          setErrorMessage([errorMessages.INTERNAL_SERVER_ERROR]);
        } else {
          const errorsArray = [];
          Object.keys(errors).forEach((key) => {
            errorsArray.push(key + " - " + errors[key]);
          });
          setErrorMessage(errorsArray);
        }
      });
  };

  const columns = useMemo(
    () => getHomecareListColumns({ onRemove: openModalRemoveHomecare }),
    [openModalRemoveHomecare]
  );

  return (
    <div
      className="min-h-screen overflow-y-auto pt-[140px] pb-[100px]"
      data-testid="homecare-page"
    >
      {isLoading ? (
        <Loader />
      ) : (
        <div className="mx-auto max-w-[1100px] space-y-4">
          <div className="flex items-center justify-between py-2.5">
            <h1 className="h1 mineShaft-color" data-testid="homecare-heading">
              Connected Homecare
            </h1>
            <button
              type="button"
              className="second-button"
              onClick={addHomecare}
              data-testid="homecare-add-button"
            >
              Add Homecare
            </button>
          </div>
          <div
            className="rounded-md border bg-white px-4 pb-4"
            data-testid="homecare-table"
          >
            <DataTable
              columns={columns}
              data={items}
              tableTitle="Connected homecare"
              onRowClick={redirectToEditHomecare}
              loading={false}
              getRowTestId={(row) => `homecare-row-${row?.connected_user_id}`}
            />
          </div>
          {modalIsOpen && (
            <RemoveHomecareModal
              modalIsOpen={modalIsOpen}
              closeModal={closeModal}
              removeHomecare={removeHomecare}
              errorMessage={errorMessage}
            />
          )}
        </div>
      )}
    </div>
  );
};
export default HomecareList;
